/* eslint-disable react/no-unused-prop-types */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/display-name */
import { Box, Button, Checkbox, HStack, Skeleton, Stack, Text } from '@chakra-ui/core';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import sortBy from 'lodash/sortBy';
import isEqual from 'lodash/isEqual';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { SearchBar } from '../../watchedAndFollowedPositions/SearchBar';
import colors from '../../../../../styles/colors';
import { RecruiterItem } from './RecruiterItem';
import { useStoreActions, useStoreState } from '../../../../../models/hooks';
import { NoRecruiter } from './NoRecruiter';
import { useSubCollectionQuerySnapshot } from '../../../../../firebase/firestore/hooks';
import { Collections, SubCollections } from '../../../../../firebase/firestore/collections';
import { BulkAssignOperations } from '../../../../../firebase/firestore/documents/accounts';
import { WhereCriteria } from '../../../../../firebase/firestore/query/criteria';
import { FirestoreOperation } from '../../../../../firebase/firestore/firestoreOperation';
import { useQueryFetchStatus } from '../../../../../app/hooks/useQueryFetchStatus';

function areArraysEqualUnordered(arr1: string[], arr2: string[]) {
  if (arr1.length !== arr2.length) {
    return false;
  }

  const sortedArr1 = sortBy(arr1);
  const sortedArr2 = sortBy(arr2);

  return isEqual(sortedArr1, sortedArr2);
}

function RecruiterListComponent() {
  const { t } = useTranslation('moniterByRecruiters');
  const {
    filteredRecruiters: recruiters,
    isRecruitersLoading: isLoading,
    recruiterFilters,
    selectedRecruiters,
    selectedPosition,
    isBulkAssignMode,
  } = useStoreState((state) => state.moniterByRecruiters);
  const { setRecruiterFilter, fetchRecruitersWithOperator, setSelectedRecruiters } = useStoreActions(
    (state) => state.moniterByRecruiters,
  );
  const accountId = useStoreState((state) => state.app?.accounts?.id);
  const appUserId = useStoreState((s) => s.app.user?.id);
  const [checkedIds, setCheckedIds] = React.useState<string[]>(selectedRecruiters);
  const listContainerRef = useRef<HTMLDivElement>(null);

  // Define criteria to listen for active bulk assign operations
  const queryObjects = React.useMemo(() => [new WhereCriteria('isBulkAssignInProgress', FirestoreOperation.GREATER, 0)], []);

  // Listen to the sub-collection
  const bulkUpdateOperations = useSubCollectionQuerySnapshot<BulkAssignOperations>(
    Collections.ACCOUNT,
    accountId || '',
    SubCollections.BULK_UPDATE_ACTIONS,
    queryObjects,
  );
  // Determine if an operation is currently in progress
  // Handle the loading state when bulkUpdateOperations returns undefined initially
  const { isFetching } = useQueryFetchStatus(bulkUpdateOperations);

  const isBulkAssignInProgress = React.useMemo(() => {
    if (!bulkUpdateOperations || !appUserId) return false;
    // Document ID = userId — loader only for the user who triggered bulk assign
    return bulkUpdateOperations.some((op) => op.id === appUserId && (op.isBulkAssignInProgress ?? 0) > 0);
  }, [bulkUpdateOperations, appUserId]);

  // Combined loading state — show skeleton/disable UI during fetch OR operation
  const isProcessing = isFetching || isBulkAssignInProgress;
  React.useEffect(() => {
    if (isBulkAssignMode) return;
    setCheckedIds(selectedRecruiters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecruiters, isBulkAssignMode]);

  React.useEffect(() => {
    if (!accountId) return;

    // Fetch recruiters whenever accountId changes OR when bulk assignment FINISHES
    // Since isBulkAssignInProgress will change from true -> false when it completes,
    // it triggers this effect.
    fetchRecruitersWithOperator({ accountId }).catch((err) => {
      console.error('Failed to fetch recruiters', err);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, isBulkAssignInProgress]);

  const recruiterIds = useMemo(() => recruiters?.map((r) => r?.id) || [], [recruiters]);

  const allChecked = useMemo(() => recruiterIds.length > 0 && checkedIds.length === recruiterIds.length, [
    checkedIds.length,
    recruiterIds.length,
  ]);
  const isIndeterminate = useMemo(() => checkedIds.length > 0 && !allChecked, [checkedIds.length, allChecked]);

  const handleSelectAllChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCheckedIds(e.target.checked ? recruiterIds : []);
    },
    [recruiterIds],
  );

  const handleToggleRecruiter = React.useCallback((id: string, only = false) => {
    if (only) {
      setCheckedIds([id]);
      return;
    }
    setCheckedIds((prev) => {
      const newSet = [...prev];
      const index = newSet.findIndex((i) => i === id);
      if (index >= 0) {
        newSet.splice(index, 1);
      } else {
        newSet.push(id);
      }

      return newSet;
    });
  }, []);

  const handleSelectRecruiters = React.useCallback(() => {
    setSelectedRecruiters([...checkedIds]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedIds]);

  React.useEffect(() => {
    if (isBulkAssignMode) {
      handleSelectRecruiters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBulkAssignMode, checkedIds]);

  const RecruiterRow = React.useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const recruiter = recruiters[index];
      if (!recruiter) return null;

      return (
        <RecruiterItem
          key={recruiter.id}
          recruiter={recruiter}
          isChecked={checkedIds.includes(recruiter.id)}
          onToggle={handleToggleRecruiter}
          onOnly={(id) => handleToggleRecruiter(id, true)}
          style={style}
        />
      );
    },
    [recruiters, checkedIds, handleToggleRecruiter],
  );

  if (selectedPosition) return null;

  return (
    <Stack
      borderRadius="md"
      bg="white"
      p={4}
      pt={3}
      px={0}
      spacing={4}
      maxWidth="300px"
      flexShrink={0}
      flexGrow={1}
      sx={{
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        height: '-webkit-fill-available',
      }}
    >
      <HStack justify="space-between" px={4}>
        <Text fontWeight="bold">{t('recruiterSelect.title')}</Text>
        <Skeleton isLoaded={!isLoading} data-testid="loading-skeleton">
          {checkedIds.length > 0 && <Text fontSize="sm">{t('recruiterSelect.total', { count: checkedIds.length })}</Text>}
        </Skeleton>
      </HStack>
      <Stack spacing={4} px={4}>
        <Box>
          <SearchBar
            onSearch={(search) => {
              setRecruiterFilter({ key: 'search', value: search });
            }}
            value={recruiterFilters.search}
            loading={false}
            placeholder={t('recruiterSelect.search')}
            isDisabled={isLoading || isProcessing}
            sx={{
              borderRadius: 30,
              height: '2rem',
              borderColor: colors.input.borderColor,
              backgroundColor: colors.input.background,
            }}
            _hover={{
              backgroundColor: colors.input.hoverBackground,
            }}
            _placeholder={{
              fontSize: 'md',
              color: colors.input.placeholderColor,
            }}
            inputGroupProps={{
              sx: {
                borderRadius: 30,
                '& .chakra-input__left-element': { height: '100%', width: '3rem' },
              },
            }}
            bottom="7px"
          />
        </Box>
        {recruiters?.length && (
          <Skeleton isLoaded={!isLoading} data-testid="loading-skeleton">
            <Checkbox isChecked={allChecked} isIndeterminate={isIndeterminate} onChange={handleSelectAllChange} size="lg">
              <Text fontSize="sm" lineHeight={1}>
                {t('recruiterSelect.selectAll')}
              </Text>
            </Checkbox>
          </Skeleton>
        )}
      </Stack>
      {recruiters?.length && (
        <>
          <Box ref={listContainerRef} flex={1} minHeight="calc(100dvh - 427px)" pl={2}>
            {!isLoading && recruiters.length > 0 ? (
              <AutoSizer>
                {({ height, width }: { height: number; width: number }) => (
                  <FixedSizeList height={height} itemCount={recruiters.length} itemSize={50} width={width}>
                    {RecruiterRow}
                  </FixedSizeList>
                )}
              </AutoSizer>
            ) : /* istanbul ignore next */ isLoading ? (
              <Stack spacing={0}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <HStack key={i} pb={3} pl={2} spacing={2} data-testid={`skeleton-loader-${i}`}>
                    <Skeleton height={5} width={5} />
                    <Skeleton height={4} width={Math.floor(Math.random() * (50 - 40 + 1)) + 40} />
                  </HStack>
                ))}
              </Stack>
            ) : (
              <NoRecruiter />
            )}
          </Box>
          {!isBulkAssignMode && (
            <HStack justify="flex-end" px={4}>
              <Button
                size="sm"
                colorScheme="blue"
                disabled={
                  isProcessing ||
                  (checkedIds.length === 0 && selectedRecruiters.length === 0) ||
                  areArraysEqualUnordered(checkedIds, selectedRecruiters)
                }
                onClick={handleSelectRecruiters}
              >
                {t('recruiterSelect.apply')}
              </Button>
            </HStack>
          )}
        </>
      )}
      {!recruiters?.length && !isLoading && <NoRecruiter />}
    </Stack>
  );
}

export const RecruiterList = React.memo(RecruiterListComponent);
